import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Container,
    Row,
    Col,
    Card,
    CardHeader,
    CardBody,
    Table,
    Spinner,
    Pagination,
    PaginationItem,
    PaginationLink,
    Button
} from 'reactstrap';
import { toast } from 'react-toastify';
import { apiURL1 } from '../../config';
import Header from '../../components/Headers/Header';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { FaDownload } from 'react-icons/fa';
// import './TeacherSubjectResultPage.css';
import Footer from 'components/Footers/Footer';
import {
    createLandscapePdf,
    standardAutoTableOptions,
    drawCenteredFooter,
} from '../../utils/pdfLayout';

const baseURL = apiURL1;

function TeacherSubjectResultPage() {
    const token = localStorage.getItem("token");

    const [teacherId, setTeacherId] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const itemsPerPage = 2;

    useEffect(() => {
        // Retrieve user info from localStorage
        const user = localStorage.getItem('userInfo');
        if (user) {
            try {
                const parsedUser = JSON.parse(user);
                setTeacherId(parsedUser._id);
            } catch (error) {
                console.error('Error parsing user info from localStorage:', error);
                toast.error('Error retrieving Staff ID.');
            }
        } else {
            toast.error('User info not found in localStorage.');
        }
    }, []);

    useEffect(() => {
        if (teacherId) {
            fetchResults();
        }
    }, [teacherId, page]);

    const fetchResults = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${baseURL}/teacher/getResultsByTeacher/${teacherId}?page=${page}&limit=${itemsPerPage}`, {
                headers: {
                    'Authorization': `Bearer ${token}`, // Include the token in the headers
                }
            });
            // console.log(' response.data ', response.data.data)
            if (response.data.data.length === 0) {
                toast.warn('No results found.');
                return;

            }
            if (response.data && response.data.data && response.data.success && Array.isArray(response.data.data)) {
                setResults(response.data.data);
                setTotalPages(response.data.pagination.totalPages);
            } else {
                setResults([]);
                setTotalPages(0);
                toast.warn(response.data.message || 'No results found.');
            }
        } catch (error) {
            console.error('Error fetching results:', error);
            toast.error('Error fetching results');
            setResults([]);
            setTotalPages(0);
        } finally {
            setLoading(false);
        }
    };

    const generatePdfReport = (result) => {
        const pdf = createLandscapePdf(jsPDF);
        const pageWidth = pdf.internal.pageSize.getWidth();

        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text(`Subject Result — ${result.subject?.name || ''}`, pageWidth / 2, 14, { align: 'center' });
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        pdf.text(
            `Class: ${result.class?.className || '-'}  |  Total: ${result.totalMarks ?? '-'}  |  Type: ${result?.type?.name || '-'}  |  Year: ${result.year || '-'}`,
            12,
            22
        );

        const tableData = (result.students || []).map((student) => [
            student?.studentName || 'N/A',
            `${student?.marksObtained ?? '-'}/${result?.totalMarks ?? '-'}`,
            student?.grade || 'N/A',
        ]);

        pdf.autoTable(standardAutoTableOptions({
            head: [['Name', 'Obtained / Total', 'Grade']],
            body: tableData,
            startY: 28,
            columnStyles: {
                0: { cellWidth: 80, halign: 'left' },
                1: { cellWidth: 40 },
                2: { cellWidth: 24 },
            },
        }));

        drawCenteredFooter(pdf, 'Ummu Dhardha Ladies Islamic College');
        pdf.save(`subject-result-${result.subject?.name || 'subject'}.pdf`);
    };

    const renderPagination = () => {
        const paginationItems = [];
        for (let i = 1; i <= totalPages; i++) {
            paginationItems.push(
                <PaginationItem key={i} active={i === page}>
                    <PaginationLink onClick={() => setPage(i)}>{i}</PaginationLink>
                </PaginationItem>
            );
        }

        return (
            <Pagination style={{ justifyContent: 'center' }}>
                <PaginationItem disabled={page === 1}>
                    <PaginationLink previous onClick={() => setPage(page - 1)} />
                </PaginationItem>
                {paginationItems}
                <PaginationItem disabled={page === totalPages}>
                    <PaginationLink next onClick={() => setPage(page + 1)} />
                </PaginationItem>
            </Pagination>
        );
    };

    return (
        <>
            <Header />
            <Container>
                <Row>
                    <Col>
                        <Card>
                            <CardHeader style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <h3>My Subject Results</h3>
                            </CardHeader>
                            <CardBody>
                                {loading ? (
                                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                                        <Spinner color="primary" />
                                    </div>
                                ) : results.length > 0 ? (
                                    results.map((result) => (
                                        <Card key={result._id} className="mb-3">
                                            <CardHeader>
                                                <h5>{result.subject?.name} - {result.class?.className} ({result?.type?.name} - {result.year})</h5>
                                            </CardHeader>
                                            <CardBody>
                                                <div className="d-flex justify-content-end mb-2">
                                                    <Button color="success" size="sm" onClick={() => generatePdfReport(result)}>
                                                        <FaDownload /> Download PDF
                                                    </Button>
                                                </div>
                                                <div className="table-responsive">
                                                    <Table className="table">
                                                        {(() => {
                                                            const compSet = new Set();
                                                            (result.students || []).forEach(st => {
                                                                (st.components || []).forEach(c => { const n = c?.component?.name || c?.componentName || c?.name; if (n) compSet.add(n); });
                                                                (st.componentObtained || []).forEach(c => { const n = c?.component?.name || c?.componentName || c?.name; if (n) compSet.add(n); });
                                                            });
                                                            const componentNames = Array.from(compSet);

                                                            return (
                                                                <>
                                                                    <thead>
                                                                        <tr style={{ backgroundColor: '#2980b9', color: 'white' }}>
                                                                            <th>Name</th>
                                                                            <th>Subject Obtained</th>
                                                                            {componentNames.map(name => (
                                                                                <th key={`comp-h-${name}`}>{name}</th>
                                                                            ))}
                                                                            <th>Grade</th>
                                                                            <th>Remarks</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {(result.students || []).map((student, idx) => {
                                                                            const totals = new Map();
                                                                            (student.components || []).forEach(c => { const n = c?.component?.name || c?.componentName || c?.name; if (n) totals.set(n, c?.totalMarks ?? '-'); });
                                                                            const obtained = new Map();
                                                                            (student.components || []).forEach(c => { const n = c?.component?.name || c?.componentName || c?.name; if (n) obtained.set(n, c?.marksObtained ?? c?.marks ?? '-'); });
                                                                            (student.componentObtained || []).forEach(c => { const n = c?.component?.name || c?.componentName || c?.name; if (n && !obtained.has(n)) obtained.set(n, c?.marks ?? c?.marksObtained ?? '-'); });
                                                                            const subjectObtained = `${(student?.marksObtained ?? '-')}/${(result?.totalMarks ?? '-')}`;
                                                                            return (
                                                                                <tr key={student?.student?._id || idx}>
                                                                                    <td>{student?.studentName || 'N/A'}</td>
                                                                                    <td>{subjectObtained}</td>
                                                                                    {componentNames.map(name => (
                                                                                        <td key={`comp-${name}-${idx}`}>{(obtained.get(name) ?? '-')}/{(totals.get(name) ?? '-')}</td>
                                                                                    ))}
                                                                                    <td>{student?.grade || 'N/A'}</td>
                                                                                    <td>{student?.remarks || '-'}</td>
                                                                                </tr>
                                                                            );
                                                                        })}
                                                                    </tbody>
                                                                </>
                                                            );
                                                        })()}
                                                    </Table>
                                                </div>
                                            </CardBody>
                                        </Card>
                                    ))
                                ) : (
                                    <p>No results found.</p>
                                )}
                                {renderPagination()}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
                <Footer />
            </Container>
        </>
    );
}

export default TeacherSubjectResultPage;
